import {
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ComponentType,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import useSWR from "swr";
import {
  Aperture,
  Check,
  Clapperboard,
  ImageOff,
  Layers3,
  Lightbulb,
  Loader2,
  Palette,
  Play,
  RotateCcw,
  Sparkles,
  Video,
} from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import {
  creativeDirectionPresetsPath,
  swrFetcher,
  type CreativeDirectionPresetItem,
  type CreativeDirectionPresetsResponse,
  type CreativeDirectionSlot,
} from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  EMPTY_DIRECTIONS,
  type DirectionPresetSelection,
  type DirectionSelections,
  type SingleDirectionSlot,
} from "@/components/studio/studioConfig";
import { useLibraryMediaUrl } from "@/hooks/useLibraryMediaUrl";
import { AssetMedia } from "@/components/media/AssetMedia";

const GENRES = ["General", "Action", "Horror", "Comedy", "Noir", "Drama", "Epic"];
const MAX_MODIFIERS = 3;

const SLOT_LABELS: Record<CreativeDirectionSlot, string> = {
  camera_body: "Camera body",
  camera_look: "Camera look",
  lens: "Lens",
  lighting: "Lighting & color",
  lighting_accent: "Lighting accent",
  movement: "Movement",
  focal_length: "Focal length",
  aperture: "Aperture",
  framing: "Framing",
  shot_angle: "Shot angle",
  modifier: "Fine tune",
};

const GROUPS: Array<{
  label: string;
  icon: ComponentType<{ className?: string }>;
  slots: CreativeDirectionSlot[];
}> = [
  { label: "Camera", icon: Clapperboard, slots: ["camera_body", "camera_look"] },
  { label: "Lens & focus", icon: Aperture, slots: ["lens", "focal_length", "aperture"] },
  { label: "Lighting & color", icon: Lightbulb, slots: ["lighting", "lighting_accent"] },
  { label: "Composition & motion", icon: Video, slots: ["movement", "framing", "shot_angle"] },
  { label: "Fine tune", icon: Layers3, slots: ["modifier"] },
];

const SLOT_SWATCHES: Record<CreativeDirectionSlot, string[]> = {
  camera_body: ["#111827", "#475569", "#d6b98c"],
  camera_look: ["#0f172a", "#334155", "#f59e0b"],
  lens: ["#1c1917", "#78716c", "#e7e5e4"],
  lighting: ["#020617", "#0e7490", "#db2777"],
  lighting_accent: ["#111827", "#f59e0b", "#fef3c7"],
  movement: ["#0f172a", "#1d4ed8", "#38bdf8"],
  focal_length: ["#172554", "#1e40af", "#93c5fd"],
  aperture: ["#292524", "#a16207", "#fde68a"],
  framing: ["#111827", "#374151", "#9ca3af"],
  shot_angle: ["#172554", "#4338ca", "#a5b4fc"],
  modifier: ["#1f2937", "#7c3aed", "#f0abfc"],
};

interface FocusedPreset {
  slot: CreativeDirectionSlot;
  item: CreativeDirectionPresetItem;
}

export function StudioDirectionPicker({
  open,
  onClose,
  value,
  onChange,
  modality,
  modelKey,
  capabilityKey,
  initialItemId,
}: {
  initialItemId?: string | null;
  open: boolean;
  onClose: () => void;
  value: DirectionSelections;
  onChange: (value: DirectionSelections) => void;
  modality: "image" | "video" | "audio";
  modelKey: string | null;
  capabilityKey: string | null;
}) {
  const visualModality = modality === "audio" ? null : modality;
  const catalogPath = open && visualModality
    ? creativeDirectionPresetsPath(visualModality, modelKey ?? undefined, capabilityKey ?? undefined)
    : null;
  const { data, error, isLoading } = useSWR<CreativeDirectionPresetsResponse>(catalogPath, swrFetcher);
  const [draft, setDraft] = useState<DirectionSelections>(value);
  const [focused, setFocused] = useState<FocusedPreset | null>(null);

  useEffect(() => {
    if (!open) return;
    setDraft(value);
    setFocused(null);
  }, [open, value]);

  useEffect(() => {
    if (!data) return;
    if (!Object.values(data.slots).some((items) => Boolean(items?.length))) {
      setDraft(EMPTY_DIRECTIONS);
      return;
    }
    setDraft((current) => pruneUnavailableSelections(current, data));
  }, [data]);

  const visibleGroups = useMemo(() => {
    if (!data) return [];
    return GROUPS.map((group) => ({
      ...group,
      slots: group.slots.filter((slot) => (data.slots[slot] ?? []).length > 0),
    })).filter((group) => group.slots.length > 0);
  }, [data]);
  const hasVisualChoices = visibleGroups.length > 0;

  useEffect(() => {
    if (!data || focused) return;
    if (initialItemId) {
      for (const [slot, items] of Object.entries(data.slots)) {
        const item = items?.find((entry) => entry.id === initialItemId);
        if (item) { setFocused({ slot: slot as CreativeDirectionSlot, item }); return; }
      }
    }
    for (const group of visibleGroups) {
      for (const slot of group.slots) {
        const selected = slot === "modifier"
          ? draft.modifiers[0]
          : draft.presets[slot as SingleDirectionSlot];
        const items = data.slots[slot] ?? [];
        const item = selected
          ? items.find((candidate) => candidate.id === selected.id)
          : items[0];
        if (item) {
          setFocused({ slot, item });
          return;
        }
      }
    }
  }, [data, draft.modifiers, draft.presets, focused, visibleGroups, initialItemId]);

  const previewItemId =
    focused?.item.thumbnail_url && focused.item.media_type === "video" ? focused.item.id : null;
  const { media: preview, error: previewFetchError, isLoading: previewLoading } =
    useLibraryMediaUrl(previewItemId, focused?.item.media_id);
  const mediaUrl = previewItemId ? preview?.url ?? null : focused?.item.thumbnail_url ?? null;
  const posterUrl = previewItemId ? preview?.poster_url ?? null : null;
  const previewError = Boolean(previewFetchError);

  const selectPreset = (slot: CreativeDirectionSlot, item: CreativeDirectionPresetItem) => {
    const selection = toSelection(slot, item);
    setDraft((current) => {
      if (slot === "modifier") {
        const selected = current.modifiers.some((candidate) => candidate.id === item.id);
        const modifiers = selected
          ? current.modifiers.filter((candidate) => candidate.id !== item.id)
          : [...current.modifiers, selection].slice(0, MAX_MODIFIERS);
        return { ...current, modifiers, bundle: null };
      }

      const presets = { ...current.presets };
      if (item.carries_aspect_ratio && data) {
        for (const [selectedSlotValue, selected] of Object.entries(presets)) {
          const selectedSlot = selectedSlotValue as SingleDirectionSlot;
          const selectedItem = (data.slots[selectedSlot] ?? []).find((candidate) => candidate.id === selected?.id);
          if (selectedItem?.carries_aspect_ratio) delete presets[selectedSlot];
        }
      }
      for (const excludedSlot of item.excludes_slots ?? []) {
        if (excludedSlot !== "modifier") delete presets[excludedSlot as SingleDirectionSlot];
      }
      if (data) {
        for (const [selectedSlotValue, selected] of Object.entries(presets)) {
          const selectedSlot = selectedSlotValue as SingleDirectionSlot;
          const selectedItem = (data.slots[selectedSlot] ?? []).find((candidate) => candidate.id === selected?.id);
          if (selectedItem?.excludes_slots?.includes(slot)) delete presets[selectedSlot];
          if (selectedItem && presetsConflict(item, selectedItem)) delete presets[selectedSlot];
        }
      }
      if (slot === "camera_body") delete presets.camera_look;
      if (slot === "camera_look") delete presets.camera_body;
      if (slot === "lighting" && presets.lighting_accent?.id === item.id) delete presets.lighting_accent;
      if (slot === "lighting_accent" && presets.lighting?.id === item.id) return current;
      presets[slot] = selection;
      return { ...current, presets, bundle: null };
    });
    setFocused({ slot, item });
  };

  const clearSlot = (slot: CreativeDirectionSlot) => {
    setDraft((current) => {
      if (slot === "modifier") return { ...current, modifiers: [], bundle: null };
      const presets = { ...current.presets };
      delete presets[slot as SingleDirectionSlot];
      return { ...current, presets, bundle: null };
    });
  };

  const done = () => {
    onChange(draft);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Creative direction"
      className="h-[100dvh] max-h-[100dvh] rounded-none sm:h-[min(820px,calc(100dvh-2rem))] sm:max-h-[calc(100dvh-2rem)] sm:max-w-[1040px] sm:rounded-2xl"
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
          <p className="max-w-3xl text-xs leading-5 text-studio-muted">
            Choose techniques for your shot. Preview media is never sent to a provider.
          </p>

          {(modality === "audio" || !data || hasVisualChoices) && (
            <DirectionGroup icon={Clapperboard} label="Genre" className="mt-5">
              <div className="flex flex-wrap gap-2">
                {GENRES.map((genre) => (
                  <button key={genre} type="button" aria-pressed={draft.genre === genre} onClick={() => setDraft((current) => ({ ...current, genre }))} className={choiceClass(draft.genre === genre)}>
                    {genre}
                  </button>
                ))}
              </div>
            </DirectionGroup>
          )}

          {modality === "audio" ? (
            <EmptyState icon={Sparkles} title="Creative Direction is for visual workflows" detail="Your genre choice can still guide an audio prompt." />
          ) : isLoading ? (
            <div className="mt-6 flex min-h-52 items-center justify-center rounded-xl border border-studio-border bg-studio-card">
              <Loader2 className="h-5 w-5 animate-spin text-studio-accent" aria-label="Loading Creative Direction" />
            </div>
          ) : error || !data ? (
            <div role="alert" className="mt-6 rounded-xl border border-studio-danger/30 bg-studio-danger/10 p-4 text-xs text-studio-danger">
              Creative direction presets could not be loaded. No direction will be sent until the catalog is available.
            </div>
          ) : (
            <>
              {(data.warnings ?? []).map((warning) => (
                <p key={warning} className="mt-3 rounded-xl border border-studio-warning/25 bg-studio-warning/10 px-3 py-2 text-[11px] leading-5 text-studio-warning">{warning}</p>
              ))}

              {!hasVisualChoices ? (
                <EmptyState icon={ImageOff} title="No compatible directions" detail="This workflow cannot safely carry Creative Direction camera or lighting choices." />
              ) : (
                <div className="mt-6 grid gap-5 lg:grid-cols-[minmax(0,1fr)_300px]">
                  <div className="min-w-0 space-y-6">
                    {visibleGroups.map((group) => (
                      <DirectionGroup key={group.label} icon={group.icon} label={group.label}>
                        <div className="space-y-4">
                          {group.slots.map((slot) => {
                            const items = data.slots[slot] ?? [];
                            const compatibleItems = items.filter((item) => presetCompatibleWithDraft(item, slot, draft, data));
                            return (
                              <div key={slot}>
                                <div className="mb-2 flex items-center justify-between gap-3">
                                  <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-studio-muted">{SLOT_LABELS[slot]}</p>
                                  {slot === "modifier" && <span className="font-mono text-[10px] text-studio-muted">{draft.modifiers.length}/{MAX_MODIFIERS}</span>}
                                </div>
                                <PresetGrid
                                  slot={slot}
                                  items={compatibleItems}
                                  selectedIds={slot === "modifier" ? draft.modifiers.map((selection) => selection.id) : [draft.presets[slot as SingleDirectionSlot]?.id].filter(Boolean) as string[]}
                                  onSelect={selectPreset}
                                  onPreview={(item) => setFocused((current) => current?.slot === slot && current.item.id === item.id ? current : { slot, item })}
                                  onClear={() => clearSlot(slot)}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </DirectionGroup>
                    ))}
                  </div>
                  <PresetPreview focused={focused} mediaUrl={mediaUrl} posterUrl={posterUrl} loading={previewLoading} error={previewError} />
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-studio-border bg-studio-panel px-4 py-3 sm:px-5">
          <button type="button" onClick={() => setDraft(EMPTY_DIRECTIONS)} className="inline-flex min-h-10 items-center gap-2 rounded-lg px-2 text-xs font-semibold text-studio-muted hover:bg-studio-card hover:text-studio-ink"><RotateCcw className="h-3.5 w-3.5" /> Reset</button>
          <button type="button" onClick={done} className="min-h-10 rounded-lg bg-studio-accent px-6 text-xs font-semibold text-studio-accent-fg hover:bg-studio-accent-hover">Done</button>
        </div>
      </div>
    </Dialog>
  );
}

function PresetGrid({ slot, items, selectedIds, onSelect, onPreview, onClear }: {
  slot: CreativeDirectionSlot;
  items: CreativeDirectionPresetItem[];
  selectedIds: string[];
  onSelect: (slot: CreativeDirectionSlot, item: CreativeDirectionPresetItem) => void;
  onPreview: (item: CreativeDirectionPresetItem) => void;
  onClear: () => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" role="listbox" aria-label={SLOT_LABELS[slot]} aria-multiselectable={slot === "modifier"}>
      <AutoPresetCard active={!selectedIds.length} onClick={onClear} />
      {items.map((item) => {
        const selected = selectedIds.includes(item.id);
        const itemDisabled = slot === "modifier" && selectedIds.length >= MAX_MODIFIERS && !selected;
        return <PresetCard key={item.id} group={`creative-direction-${slot}`} slot={slot} item={item} selected={selected} disabled={itemDisabled} onSelect={onSelect} onPreview={onPreview} />;
      })}
    </div>
  );
}

function PresetCard({ group, slot, item, selected, disabled, onSelect, onPreview }: {
  group: string;
  slot: CreativeDirectionSlot;
  item: CreativeDirectionPresetItem;
  selected: boolean;
  disabled: boolean;
  onSelect: (slot: CreativeDirectionSlot, item: CreativeDirectionPresetItem) => void;
  onPreview: (item: CreativeDirectionPresetItem) => void;
}) {
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = Boolean(item.thumbnail_url && !imageFailed);
  return (
    <button type="button" role="option" aria-selected={selected} disabled={disabled} data-preset-group={group} onClick={() => onSelect(slot, item)} onFocus={() => onPreview(item)} onPointerMove={() => onPreview(item)} onKeyDown={(event) => moveGridFocus(event, group)} className={cn("group relative min-h-32 overflow-hidden rounded-xl border bg-studio-card text-left transition-all focus-visible:outline-none focus-visible:shadow-glow-sm disabled:cursor-not-allowed disabled:opacity-45", selected ? "border-studio-accent shadow-glow-sm" : "border-studio-border hover:border-studio-border-light")}>
      <span className="relative block aspect-[16/9] overflow-hidden bg-studio-panel" style={!showImage ? gradientStyle(item.swatch, slot) : undefined}>
        {showImage ? <img src={item.thumbnail_url ?? ""} alt="" onError={() => setImageFailed(true)} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" /> : <span className="flex h-full items-center justify-center"><Palette className="h-5 w-5 text-white/75" /></span>}
        {item.media_type === "video" && item.thumbnail_url && <span className="absolute bottom-2 left-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/65 text-white"><Play className="h-3 w-3 fill-current" /></span>}
        {selected && <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-studio-accent text-studio-accent-fg"><Check className="h-3.5 w-3.5" /></span>}
      </span>
      <span className="block px-2.5 py-2"><span className="block truncate text-[11px] font-semibold text-studio-ink">{item.title}</span><span className="block text-[10px] text-studio-muted">{item.source}</span>{item.tags?.length ? <span className="mt-0.5 block truncate text-[9px] text-studio-muted">{item.tags.slice(0, 3).join(" · ")}</span> : null}</span>
    </button>
  );
}

function AutoPresetCard({ active, onClick }: { active: boolean; onClick: () => void }) {
  return (
    <button type="button" role="option" aria-selected={active} onClick={onClick} className={cn("relative min-h-32 overflow-hidden rounded-xl border bg-studio-card text-left transition-colors focus-visible:outline-none focus-visible:shadow-glow-sm", active ? "border-studio-accent shadow-glow-sm" : "border-studio-border hover:border-studio-border-light")}>
      <span className="flex aspect-[16/9] items-center justify-center bg-gradient-to-br from-studio-panel to-studio-card"><Sparkles className="h-5 w-5 text-studio-accent" /></span>
      <span className="block px-2.5 py-2 text-[11px] font-semibold text-studio-ink">Auto</span>
      {active && <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-studio-accent text-studio-accent-fg"><Check className="h-3.5 w-3.5" /></span>}
    </button>
  );
}

function PresetPreview({ focused, mediaUrl, posterUrl, loading, error }: {
  focused: FocusedPreset | null;
  mediaUrl: string | null;
  posterUrl: string | null;
  loading: boolean;
  error: boolean;
}) {
  return (
    <aside className="order-first lg:order-last lg:sticky lg:top-0 lg:self-start" aria-live="polite">
      <div className="overflow-hidden rounded-2xl border border-studio-border bg-studio-card">
        <div className="relative aspect-video bg-black/30 lg:aspect-[4/5]" style={!mediaUrl && focused ? gradientStyle(focused.item.swatch, focused.slot) : undefined}>
          {loading ? <span className="flex h-full items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-studio-accent" /></span> : error || !focused ? <span className="flex h-full flex-col items-center justify-center gap-2 text-[11px] text-studio-muted"><ImageOff className="h-5 w-5" /> Preview unavailable</span> : mediaUrl ? <AssetMedia type={focused.item.media_type === "video" ? "video" : "image"} url={mediaUrl} poster={posterUrl ?? focused.item.thumbnail_url} alt={`${focused.item.title} visual reference`} autoPlay className="bg-transparent" /> : <span className="flex h-full items-center justify-center"><Palette className="h-8 w-8 text-white/75" /></span>}
        </div>
        <div className="p-3.5">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-studio-accent">{focused ? SLOT_LABELS[focused.slot] : "Preview"}</p>
          <h3 className="mt-1 text-sm font-semibold text-studio-ink">{focused?.item.title ?? "Choose a direction"}</h3>
          <p className="mt-1.5 text-[11px] leading-5 text-studio-muted">{focused?.item.description ?? "Focus a card to inspect its production direction."}</p>
          {focused?.item.warning && <p className="mt-2 rounded-lg bg-studio-warning/10 px-2.5 py-2 text-[10px] leading-4 text-studio-warning">{focused.item.warning}</p>}
          {focused?.item.tags?.length ? <p className="mt-2 text-[9px] leading-4 text-studio-faint">{focused.item.tags.join(" · ")}</p> : null}
        </div>
      </div>
    </aside>
  );
}

function DirectionGroup({ icon: Icon, label, children, className }: { icon: ComponentType<{ className?: string }>; label: string; children: ReactNode; className?: string }) {
  return <fieldset className={className}><legend className="mb-2.5 flex items-center gap-2 text-xs font-semibold text-studio-ink"><Icon className="h-4 w-4 text-studio-accent" /> {label}</legend>{children}</fieldset>;
}

function EmptyState({ icon: Icon, title, detail }: { icon: ComponentType<{ className?: string }>; title: string; detail: string }) {
  return <div className="mt-6 rounded-xl border border-dashed border-studio-border bg-studio-card p-5 text-center"><Icon className="mx-auto h-5 w-5 text-studio-accent" /><p className="mt-2 text-xs font-semibold text-studio-ink">{title}</p><p className="mt-1 text-[11px] leading-5 text-studio-muted">{detail}</p></div>;
}

function presetsConflict(
  first: CreativeDirectionPresetItem,
  second: CreativeDirectionPresetItem
): boolean {
  const firstKey = first.stable_key;
  const secondKey = second.stable_key;
  if (!firstKey || !secondKey) return false;
  return Boolean(
    first.incompatible_with?.includes(secondKey)
    || second.incompatible_with?.includes(firstKey)
  );
}

function presetCompatibleWithDraft(
  item: CreativeDirectionPresetItem,
  slot: CreativeDirectionSlot,
  draft: DirectionSelections,
  data: CreativeDirectionPresetsResponse
): boolean {
  if (draft.presets[slot as SingleDirectionSlot]?.id === item.id) return true;
  for (const [selectedSlotValue, selection] of Object.entries(draft.presets)) {
    const selectedSlot = selectedSlotValue as SingleDirectionSlot;
    if (!selection || selectedSlot === slot) continue;
    const selectedItem = (data.slots[selectedSlot] ?? []).find(
      (candidate) => candidate.id === selection.id
    );
    if (!selectedItem) continue;
    if (selection.id === item.id) return false;
    if (item.excludes_slots?.includes(selectedSlot)) return false;
    if (selectedItem.excludes_slots?.includes(slot)) return false;
    if (item.carries_aspect_ratio && selectedItem.carries_aspect_ratio) return false;
    if (presetsConflict(item, selectedItem)) return false;
  }
  return true;
}

function toSelection(slot: CreativeDirectionSlot, item: CreativeDirectionPresetItem): DirectionPresetSelection {
  return { id: item.id, title: item.title, slot, stable_key: item.stable_key };
}

function pruneUnavailableSelections(selections: DirectionSelections, data: CreativeDirectionPresetsResponse): DirectionSelections {
  const presets: DirectionSelections["presets"] = {};
  for (const [slotValue, selection] of Object.entries(selections.presets)) {
    const slot = slotValue as SingleDirectionSlot;
    if (selection && (data.slots[slot] ?? []).some((item) => item.id === selection.id)) presets[slot] = selection;
  }
  const modifierIds = new Set((data.slots.modifier ?? []).map((item) => item.id));
  const modifiers = selections.modifiers.filter((selection) => modifierIds.has(selection.id)).slice(0, MAX_MODIFIERS);
  const bundle = selections.bundle && (data.bundles ?? []).some((item) => item.id === selections.bundle?.id) ? selections.bundle : null;
  if (Object.keys(presets).length === Object.keys(selections.presets).length && modifiers.length === selections.modifiers.length && bundle === selections.bundle) return selections;
  return { ...selections, presets, modifiers, bundle };
}

function gradientStyle(swatch?: string[], slot?: CreativeDirectionSlot): CSSProperties {
  const colors = swatch?.filter(Boolean).slice(0, 4);
  const palette = colors?.length ? colors : SLOT_SWATCHES[slot ?? "camera_look"];
  return { backgroundImage: `linear-gradient(135deg, ${palette.join(", ")})` };
}

function choiceClass(active: boolean): string {
  return cn("min-h-10 rounded-lg border px-3 text-[11px] font-semibold transition-colors focus-visible:outline-none focus-visible:shadow-glow-sm", active ? "border-studio-accent/45 bg-studio-accent-soft text-studio-accent" : "border-studio-border bg-studio-card text-studio-muted hover:border-studio-border-light hover:text-studio-ink");
}

function moveGridFocus(event: KeyboardEvent<HTMLButtonElement>, group: string) {
  if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
  const cards = Array.from(document.querySelectorAll<HTMLButtonElement>(`[data-preset-group="${group}"]`));
  const index = cards.indexOf(event.currentTarget);
  if (index < 0) return;
  const columns = window.innerWidth >= 640 ? 3 : 2;
  const delta = event.key === "ArrowLeft" ? -1 : event.key === "ArrowRight" ? 1 : event.key === "ArrowUp" ? -columns : columns;
  const next = cards[index + delta];
  if (!next) return;
  event.preventDefault();
  next.focus();
}
